/**
 * UsageTimeChart
 *
 * ```tsx
 * <UsageTimeChart
 *   actualUsageData={[
 *     { date: '2025-01-01', value: 100 },
 *     { date: '2025-01-02', value: 120 },
 *   ]}
 *   totalAvailableData={[
 *     { date: '2025-01-01', count: 200 },
 *     { date: '2025-01-02', count: 200 },
 *   ]}
 *   yAxisTitle="Usage Time(hr)"
 * />
 * ```
 */

import { DualAxes } from '@antv/g2plot'
import dayjs from 'dayjs'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { useThemeMode } from '@/contexts/ThemeContext'
import { getChartThemeColors, getDarkDualAxesTheme } from '@/utils/chart-theme'

import { USAGE_CHART_COLORS } from '../constants/colors'

// ==================== Types ====================
interface ActualUsageDataItem {
  date: string
  value: number
}

interface TotalAvailableDataItem {
  date: string
  count: number
}

interface UsageTimeChartProps {
  actualUsageData: ActualUsageDataItem[]
  totalAvailableData: TotalAvailableDataItem[]
  yAxisTitle?: string
  height?: number
}

// ==================== 主元件 ====================
export function UsageTimeChart({
  actualUsageData,
  totalAvailableData,
  yAxisTitle,
  height = 450,
}: UsageTimeChartProps) {
  const { t } = useTranslation('privateRegion')
  const { themeMode } = useThemeMode()
  const containerRef = useRef<HTMLDivElement>(null)

  // Use translation if no title is provided
  const axisTitle = yAxisTitle || t('usageTime.yAxisLabel')

  useEffect(() => {
    if (!containerRef.current) return

    // Calculate max value with fallback for empty data
    const allValues = [
      ...actualUsageData.map((item) => item.value),
      ...totalAvailableData.map((item) => item.count),
    ]
    const maxValue = allValues.length > 0 ? Math.max(...allValues) + 200 : 200
    const chartTheme = getChartThemeColors()
    const dualAxesTheme = getDarkDualAxesTheme(themeMode, chartTheme.grid)
    const themedAxisStyle =
      themeMode === 'dark'
        ? {
            fill: chartTheme.axisText,
          }
        : undefined

    const dualAxes = new DualAxes(containerRef.current, {
      data: [actualUsageData, totalAvailableData],
      xField: 'date',
      yField: ['value', 'count'],
      height: height - 90,
      geometryOptions: [
        {
          geometry: 'line',
          smooth: false,
          color: USAGE_CHART_COLORS.actualUsage,
        },
        {
          geometry: 'line',
          smooth: false,
          color: USAGE_CHART_COLORS.totalAvailable,
        },
      ],
      xAxis: {
        tickCount: 15,
        type: 'time',
        nice: false,
        label: {
          formatter: (text: string) => {
            const day = Number(dayjs(text).format('D'))
            return day >= 1 ? dayjs(text).format('DD') : ''
          },
          style: {
            ...themedAxisStyle,
            fontWeight: 600,
            fontSize: 12,
            lineHeight: 16,
            letterSpacing: 0,
            textAlign: 'center',
          },
          offset: 16,
        },
        title: {
          text: t('common.date'),
          style: {
            ...themedAxisStyle,
            fontWeight: 600,
            fontSize: 12,
            lineHeight: 16,
            letterSpacing: 0,
            textAlign: 'center',
          },
          offset: 40,
        },
        line: dualAxesTheme.axisLine,
      },
      yAxis: {
        value: {
          min: 0,
          max: maxValue,
          label: {
            style: {
              ...themedAxisStyle,
              fontWeight: 600,
              fontSize: 12,
              lineHeight: 16,
              letterSpacing: 0,
              textAlign: 'center',
            },
            offsetX: -16,
          },
          title: {
            text: axisTitle,
            style: {
              ...themedAxisStyle,
              fontWeight: 600,
              fontSize: 12,
              lineHeight: 16,
              letterSpacing: 0,
              textAlign: 'center',
            },
            offset: 52,
          },
          grid: dualAxesTheme.grid,
        },
        count: {
          min: 0,
          max: maxValue,
          label: null,
          grid: null,
        },
      },
      legend: {
        position: 'bottom',
        offsetY: 4,
        maxItemWidth: 600,
        itemName: {
          style: {
            fill: chartTheme.legendText,
            fontWeight: 600,
            fontSize: 14,
            lineHeight: 20,
          },
        },
        custom: true,
        items: [
          {
            name: t('usageTime.actualUsageTime'),
            value: 'value',
            marker: {
              symbol: 'circle',
              style: {
                fill: USAGE_CHART_COLORS.actualUsage,
              },
            },
          },
          {
            name: t('usageTime.totalAvailableTime'),
            value: 'count',
            marker: {
              symbol: 'circle',
              style: {
                fill: USAGE_CHART_COLORS.totalAvailable,
              },
            },
          },
        ],
      },
      tooltip: {
        domStyles: {
          'g2-tooltip': {
            backgroundColor: 'transparent',
            boxShadow: 'none',
            padding: 0,
            border: 'none',
          },
        },
        customContent: (title, items) => {
          const noDataText = t('common.noData')
          const occupancyRateText = t('usageTime.occupancyRate')
          const actualTimeText = t('usageTime.actualTimeOnSale')
          const totalTimeText = t('usageTime.totalAvailableTime')

          if (!items || items.length < 2 || !items[0] || !items[1]) {
            return `<div style="background-color: #000; color: #fff; padding: 8px 12px; border-radius: 8px; font-size: 14px;">${noDataText}</div>`
          }
          const actualValue = Number(items[0].value) || 0
          const totalValue = Number(items[1].value) || 0
          const rate = totalValue > 0 ? (actualValue / totalValue) * 100 : 0
          return `
            <div style="min-width: 256px; display: flex; flex-direction: column; gap: 8px; background-color: #000; color: #fff; padding: 8px 12px; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.15); margin-top: -20px;">
              <div style="display: flex; align-items: flex-start; justify-content: space-between;">
                <div style="font-size: 12px; line-height: 16px; font-weight: 600;">${dayjs(title).format('YYYY/MM/DD')}</div>
                <div style="font-size: 12px; line-height: 16px; font-weight: 600;">
                    <span style="color: rgba(255, 255, 255, 0.65);">${occupancyRateText}</span>
                    <span style="font-size: 12px; line-height: 16px; font-weight: 600; margin-left: 4px; color: white;">${(isNaN(rate) ? 0 : rate).toFixed(2)}%</span>
                </div>
              </div>
              <div style="height: 1px; width: 100%; background-color: rgba(255, 255, 255, 0.35);"></div>
              ${items
                .map(
                  (item) => `
                    <div style="display: flex; justify-content: space-between; align-items: center; gap: 8px;">
                      <div style="display: flex; align-items: center; gap: 8px;">
                        <div style="width: 6px; height: 6px; border-radius: 50%; background-color: ${item.name === 'value' ? USAGE_CHART_COLORS.actualUsage : USAGE_CHART_COLORS.totalAvailable};"></div>
                        <div style="font-size: 12px; line-height: 16px; font-weight: 600; color: rgba(255, 255, 255, 0.65);">${item.name === 'value' ? actualTimeText : totalTimeText}</div>
                      </div>
                      <div style="font-size: 12px; line-height: 16px; font-weight: 600; color: white;">${(Number(item.value) || 0).toFixed(2)}</div>
                    </div>
                  `,
                )
                .join('')}
            </div>
          `
        },
      },
    })

    dualAxes.render()

    return () => {
      dualAxes.destroy()
    }
  }, [actualUsageData, totalAvailableData, axisTitle, height, t, themeMode])

  return (
    <div
      className="w-full bg-panel-white p-6 rounded-2xl border border-neutral-200"
      style={{ height: `${height}px` }}
    >
      <div ref={containerRef} className="w-full h-full" />
    </div>
  )
}

export default UsageTimeChart
